import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, RefreshCw, Mail } from "lucide-react";
import { adminApi, formatApiErrorDetail } from "../../../lib/adminApi";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export default function ContactsManager() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get("/admin/contacts");
      setItems(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    try {
      await adminApi.delete(`/admin/contacts/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    }
  };

  return (
    <div data-testid="contacts-manager-section">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display text-lg font-semibold text-[#0B192C]">Contact inbox ({items.length})</h3>
        <Button variant="outline" onClick={load} className="rounded-full h-10" data-testid="contacts-refresh-button">
          <RefreshCw size={14} className={`mr-1.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      <div className="space-y-3">
        {loading ? (
          <div className="bg-white rounded-2xl border border-slate-200 p-6 text-sm text-slate-500">Loading…</div>
        ) : items.length === 0 ? (
          <div className="bg-white rounded-2xl border border-slate-200 p-6 text-sm text-slate-500" data-testid="contacts-empty">No submissions yet.</div>
        ) : items.map((c) => (
          <div key={c.id} className="bg-white rounded-2xl border border-slate-200 p-5" data-testid={`contact-row-${c.id}`}>
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="font-semibold text-[#0B192C]">{c.name} <span className="text-slate-400 font-normal">· {new Date(c.created_at).toLocaleString()}</span></div>
                <div className="text-xs text-slate-500 mt-0.5">
                  <a href={`mailto:${c.email}`} className="hover:text-[#FF6B00] inline-flex items-center gap-1"><Mail size={12} />{c.email}</a>
                  {c.phone && <span className="ml-3">· {c.phone}</span>}
                </div>
                {c.subject && <div className="mt-2 text-sm font-medium text-[#0B192C]">{c.subject}</div>}
                <p className="mt-2 text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{c.message}</p>
              </div>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <button className="p-2 text-slate-500 hover:text-red-600 shrink-0" data-testid={`contact-delete-${c.id}`}>
                    <Trash2 size={16} />
                  </button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete this submission?</AlertDialogTitle>
                    <AlertDialogDescription>This cannot be undone.</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={() => remove(c.id)} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
